import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Trash2, AlertTriangle } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Switch } from '../../ui/switch'
import { planManagementApi, type SubscriptionPlan } from '../../../services/planManagementApi'

interface DeletePlanModalProps {
  plan: SubscriptionPlan
  onClose: () => void
}

export function DeletePlanModal({ plan, onClose }: DeletePlanModalProps) {
  const [forceDelete, setForceDelete] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      planManagementApi.deletePlan(id, force),
    onSuccess: () => {
      onClose()
    },
    onError: (error: any) => {
      console.error('Failed to delete plan:', error)
    }
  })

  const handleDelete = () => {
    if (confirmText !== plan.name) {
      return
    }

    deleteMutation.mutate({ id: plan.id, force: forceDelete })
  }

  const hasUsage = plan.usage_stats && (plan.usage_stats.total_users > 0 || plan.usage_stats.total_organizations > 0)
  const isConfirmValid = confirmText === plan.name

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Plan</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-3">
            <p className="text-gray-700">
              You are about to delete the subscription plan:
            </p>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">{plan.display_name}</p>
              <p className="text-sm text-gray-500 font-mono">{plan.name}</p>
            </div>
          </div>

          {hasUsage && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Plan is in use</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This plan is currently used by{' '}
                    <strong>{plan.usage_stats!.total_users} users</strong> and{' '}
                    <strong>{plan.usage_stats!.total_organizations} organizations</strong>.
                  </p>
                  
                  <div className="flex items-center justify-between mt-3">
                    <label className="text-sm font-medium text-yellow-800">
                      Force delete (removes from all users)
                    </label>
                    <Switch
                      checked={forceDelete}
                      onCheckedChange={setForceDelete}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Type "{plan.name}" to confirm deletion
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type "${plan.name}" here`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {deleteMutation.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                {(deleteMutation.error as any)?.response?.data?.detail || 'Failed to delete plan'}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={!isConfirmValid || deleteMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleteMutation.isPending ? 'Deleting...' : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Plan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}